/* eslint-disable import/no-anonymous-default-export */
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@material-ui/core";
import Icon from "@material-ui/icons/ShoppingBasket";
import { DropzoneArea } from "material-ui-dropzone";
import React, { FC } from "react";
import {
  Create,
  CreateButton,
  CreateProps,
  Datagrid,
  Edit,
  EditProps,
  Filter,
  FunctionField,
  List,
  ListProps,
  SaveButton,
  SelectInput,
  Show,
  ShowProps,
  SimpleForm,
  SimpleShowLayout,
  TextField,
  TextInput,
  Toolbar,
  TopToolbar,
  useDataProvider,
  useListContext,
  useMutation,
  useNotify,
} from "react-admin";
import CrudBreadcrumb from "../components/CrudBreadcrumb";
import StatusField from "../components/StatusField";
import BusinessUnitInput from "../components/inputs/BusinessUnitInput";
import ProductTypeInput from "../components/inputs/ProductTypeInput";

const isSR = process.env.REACT_APP_COMPANY_CODE === "SNR";

const productTypeChoices = isSR
  ? [
      { id: "instrument", name: "Device" },
      { id: "implant", name: "Consumable" },
      { id: "others", name: "Others" },
    ]
  : [
      { id: "instrument", name: "Instrument" },
      { id: "implant", name: "Implant" },
      { id: "others", name: "Others" },
    ];

const productTypeMap = productTypeChoices.reduce<Record<string, string>>(
  (acc, choice) => {
    acc[choice.id] = choice.name;
    return acc;
  },
  {},
);

const ResourceList: FC<ListProps> = (props) => {
  const notify = useNotify();
  const [mutate] = useMutation();
  const dataProvider = useDataProvider();
  const [isImportModalOpen, setIsImportModalOpen] = React.useState(false);
  const [selectedCsvFile, setSelectedCsvFile] = React.useState<File | null>(
    null,
  );
  const [isUploading, setIsUploading] = React.useState(false);
  const [isExporting, setIsExporting] = React.useState(false);

  const handleOpenImportModal = () => {
    setIsImportModalOpen(true);
  };

  const handleCloseImportModal = () => {
    if (isUploading) {
      return;
    }
    setIsImportModalOpen(false);
    setSelectedCsvFile(null);
  };

  const handleCsvFileChange = (files: File[]) => {
    const file = files && files[0];
    setSelectedCsvFile(file || null);
  };

  const handleUploadCsv = () => {
    if (!selectedCsvFile) {
      notify("Please select a CSV file first.", "warning");
      return;
    }

    const formData = new FormData();
    formData.append("csv", selectedCsvFile);

    setIsUploading(true);
    mutate(
      {
        type: "create",
        resource: "child-codes/upload-csv",
        payload: {
          data: formData,
        },
      },
      {
        onSuccess: () => {
          notify(
            "Product child code import started in background. We will notify you when it's done.",
            "info",
          );
          setIsImportModalOpen(false);
          setSelectedCsvFile(null);
          setIsUploading(false);
        },
        onFailure: (error: any) => {
          notify(`Error: ${error?.message || "Unknown error"}`, "warning");
          setIsUploading(false);
        },
      },
    );
  };

  const handleExportCsv = async () => {
    if (isExporting) {
      return;
    }

    setIsExporting(true);
    notify(
      "Product child code export started in background. We will notify you when it's ready.",
      "info",
    );

    try {
      await dataProvider.exportCsv("child-codes/export-csv");
    } catch (error: any) {
      notify(`Error: ${error?.message || "Unknown error"}`, "warning");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <>
      <List
        {...props}
        exporter={false}
        sort={{ field: "id", order: "desc" }}
        filters={<ListFilter />}
        empty={
          <ProductChildCodeEmpty onOpenImportModal={handleOpenImportModal} />
        }
        actions={
          <ProductChildCodeListActions
            onOpenImportModal={handleOpenImportModal}
            onExportCsv={handleExportCsv}
            isExporting={isExporting}
            filters={<ListFilter />}
          />
        }
        bulkActionButtons={false}
      >
        <Datagrid rowClick="show">
          <TextField source="id" />
          <TextField source="product_code" label="Product Code" />
          <TextField source="business_unit.name" label="Business Unit" />
          <FunctionField
            label="Type"
            render={(record: any) =>
              productTypeMap[record?.type] ?? record?.type
            }
          />
          <TextField source="description" />
          <StatusField source="status" />
        </Datagrid>
      </List>

      <Dialog
        open={isImportModalOpen}
        onClose={handleCloseImportModal}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Import CSV</DialogTitle>
        <DialogContent>
          <Box mb={2}>
            <Typography variant="body2">
              Required CSV columns (case sensitive):
            </Typography>
            <ul>
              <li>Product Code</li>
              <li>Business Unit</li>
              <li>Type</li>
              <li>Description</li>
              <li>Status</li>
            </ul>
          </Box>
          <DropzoneArea
            acceptedFiles={[".csv", "text/csv"]}
            filesLimit={1}
            onChange={handleCsvFileChange}
            dropzoneText="Drag and drop a CSV file here or click"
            showPreviewsInDropzone
            showAlerts={false}
            maxFileSize={20 * 1024 * 1024}
          />
          {selectedCsvFile && (
            <Box mt={2}>
              <Typography variant="body2">
                Selected file: {selectedCsvFile.name}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseImportModal}
            color="primary"
            disabled={isUploading}
          >
            Close
          </Button>
          <Button
            onClick={handleUploadCsv}
            color="primary"
            variant="contained"
            disabled={!selectedCsvFile || isUploading}
          >
            {isUploading ? "Uploading..." : "Upload"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

interface ProductChildCodeEmptyProps {
  onOpenImportModal: () => void;
}

const ProductChildCodeEmpty: FC<ProductChildCodeEmptyProps> = ({
  onOpenImportModal,
}) => {
  const { basePath } = useListContext();

  return (
    <Box p={3}>
      <Typography variant="h6" gutterBottom>
        No Product Child Code yet
      </Typography>
      <Typography variant="body2" color="textSecondary" paragraph>
        Start by creating a new item or import records from a CSV file.
      </Typography>
      <TopToolbar>
        <CreateButton basePath={basePath} />
        <Button
          variant="contained"
          color="primary"
          size="small"
          onClick={onOpenImportModal}
        >
          Import CSV
        </Button>
      </TopToolbar>
    </Box>
  );
};

const ListFilter: FC<any> = (props) => (
  <Filter {...props}>
    <TextInput source="id" label="ID" />
    <TextInput source="search" label="Code and Description" />
    <SelectInput source="type" choices={productTypeChoices} />
    <BusinessUnitInput source="business_unit_id" />
  </Filter>
);

interface ProductChildCodeListActionsProps {
  resource?: string;
  displayedFilters?: Record<string, boolean>;
  filterValues?: Record<string, any>;
  showFilter?: (filterName: string, defaultValue: any) => void;
  onOpenImportModal: () => void;
  onExportCsv: () => void;
  isExporting: boolean;
  filters?: React.ReactElement;
  basePath?: string;
}

const ProductChildCodeListActions: FC<ProductChildCodeListActionsProps> = (
  props,
) => {
  const {
    resource,
    displayedFilters,
    filterValues,
    showFilter,
    onOpenImportModal,
    onExportCsv,
    isExporting,
    filters,
    basePath,
  } = props;

  return (
    <TopToolbar>
      <CreateButton basePath={basePath} />
      <Button
        variant="contained"
        color="primary"
        size="small"
        onClick={onOpenImportModal}
        disabled={isExporting}
      >
        Import CSV
      </Button>
      <Button
        variant="contained"
        color="primary"
        size="small"
        onClick={onExportCsv}
        disabled={isExporting}
      >
        {isExporting ? "Exporting..." : "Export CSV"}
      </Button>
      {filters &&
        React.cloneElement(filters, {
          resource,
          showFilter,
          displayedFilters,
          filterValues,
          context: "button",
        })}
    </TopToolbar>
  );
};

const ResourceShow: FC<ShowProps> = (props) => {
  return (
    <>
      <CrudBreadcrumb
        resourceName={Common.resourceName}
        resourceId={props.id}
        mode="show"
      />
      <Show {...props}>
        <SimpleShowLayout>
          <TextField source="id" />
          <TextField source="product_code" label="Product Code" />
          <TextField source="business_unit.name" label="Business Unit" />
          <FunctionField
            label="Type"
            render={(record: any) =>
              productTypeMap[record?.type] ?? record?.type
            }
          />
          <TextField source="description" />
          <StatusField source="status" />
        </SimpleShowLayout>
      </Show>
    </>
  );
};

const ResourceEdit: FC<EditProps> = (props) => {
  const IssueEditToolbar: FC<any> = (props) => (
    <Toolbar {...props}>
      <SaveButton disabled={props.pristine} />
    </Toolbar>
  );
  return (
    <>
      <CrudBreadcrumb
        resourceName={Common.resourceName}
        resourceId={props.id}
        mode="edit"
      />
      <Edit {...props}>
        <SimpleForm redirect="show" toolbar={<IssueEditToolbar />}>
          <TextField source="id" />
          <TextInput source="product_code" label="Product Code" />
          <BusinessUnitInput source="business_unit_id" />
          <ProductTypeInput source="type" />
          <TextInput source="description" />
          <SelectInput
            source="status"
            choices={[
              { id: "active", name: "Active" },
              { id: "inactive", name: "Inactive" },
            ]}
          />
        </SimpleForm>
      </Edit>
    </>
  );
};

const ResourceCreate: FC<CreateProps> = (props) => {
  return (
    <>
      <CrudBreadcrumb
        resourceName={Common.resourceName}
        resourceId={""}
        mode="create"
      />
      <Create {...props}>
        <SimpleForm>
          <TextInput source="product_code" label="Product Code" />
          <BusinessUnitInput source="business_unit_id" />
          <SelectInput source="type" choices={productTypeChoices} />
          <TextInput source="description" />
          <SelectInput
            source="status"
            choices={[
              { id: "active", name: "Active" },
              { id: "inactive", name: "Inactive" },
            ]}
          />
        </SimpleForm>
      </Create>
    </>
  );
};

const Common = {
  resourceName: "child-codes",
  baseBreadCrumb: [
    {
      link: `/child-codes`,
      title: "Product Child Code",
    },
  ],
};

export default {
  name: Common.resourceName,
  options: { label: "Product Child Code" },
  icon: Icon,
  list: ResourceList,
  show: ResourceShow,
  create: ResourceCreate,
  edit: ResourceEdit,
};
